import type {
    OperationCatalogRow,
    OperationCategory,
    OperationStatus,
    OperationText,
    UserRole,
} from '@/api/models';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';

/**
 * The Templates tab's view model and its presentational config.
 *
 * Everything here is pure: the page hook derives rows through it, the View
 * renders them, and nothing in between reaches back to the API shapes. Two of
 * these decisions are worth stating outright because a later change could
 * quietly undo them.
 *
 * **A system row and a template row are one table row type with a `kind`, not
 * two tables.** The catalog's whole claim is "here is every way the ledger gets
 * written to", and splitting it across two tables would make the reader assemble
 * that claim themselves. What differs — the chip, the version column, whether
 * there is an Edit button — differs *per cell*.
 *
 * **A missing value is an en dash, never a zero and never a sentence.** A
 * template with no version in force has no version number; a row nothing has
 * recorded against has no last-used date. `—` says "nothing here"; `0` and
 * "never" both say something the client does not know.
 */

/** The em-dash stand-in for a value that does not exist on this row. */
export const NIL_TEXT = '—';

// ── the row ─────────────────────────────────────────────────────────────────

export type OperationRow = {
    /** Stable across both kinds; also the row key and the testid discriminator. */
    key: string;
    kind: 'SYSTEM' | 'TEMPLATE';
    /** The Georgian name — the row's title. */
    nameKa: string;
    /** The English name — the subtitle under it. */
    nameEn: string;
    /** `null` on a system row: it has no status, and must not be given one. */
    status: OperationStatus | null;
    /**
     * What kind of operation this is. `null` on a system row — those post from
     * code and are not the tenant's to file.
     */
    category: OperationCategory | null;
    /** `v3` / `—`. */
    versionText: string;
    /** `01/06/2026` / `—`. */
    effectiveFromText: string;
    /** Pair count as text, or `—` where the row has no recipe of its own. */
    pairsText: string;
    rolesAllowed: UserRole[];
    lastUsedText: string;
    /**
     * How many times this operation was recorded in the ranking window, as text.
     *
     * Rendered because the catalog now **orders itself** by this number, and an
     * unexplained reordering is the part of a self-sorting list that users find
     * unsettling. Showing the figure turns "why is this at the top?" into a
     * readable answer. A muted number rather than a badge: it explains the
     * order, it is not a thing to act on.
     *
     * Zero renders as an en dash, like every other absent figure on this screen
     * — `0` and "nothing recorded" are the same fact here, and a column of
     * zeroes reads as noise.
     */
    recordCountText: string;
    /** The posting steps behind a system row; empty on a template. */
    recipe: OperationText[];
    /** `true` when there is an Edit affordance to render at all. */
    isEditable: boolean;
};

/**
 * Fold a catalog row into what the table renders.
 *
 * `canEdit` comes from `operationsAccess` and is `false` for everyone on this
 * task — the button is rendered disabled with a tooltip that says why, rather
 * than hidden, so the column does not change shape when task 02 enables it.
 */
export const toOperationRow = (
    row: OperationCatalogRow,
    canEdit: boolean,
): OperationRow => {
    const base = {
        key: row.key,
        nameKa: row.nameKa,
        nameEn: row.nameEn,
        rolesAllowed: [] as UserRole[],
        lastUsedText: row.lastUsedAt
            ? isoToDisplayDate(row.lastUsedAt)
            : NIL_TEXT,
        recordCountText:
            row.recordCount > 0 ? String(row.recordCount) : NIL_TEXT,
    };

    if (row.kind === 'SYSTEM') {
        return {
            ...base,
            kind: 'SYSTEM',
            status: null,
            category: null,
            versionText: NIL_TEXT,
            effectiveFromText: NIL_TEXT,
            pairsText: NIL_TEXT,
            recipe: row.recipe,
            // Not "disabled" — absent. A system row has no editor to open now
            // or later, so offering a greyed button would advertise something
            // that is never coming.
            isEditable: false,
        };
    }

    const version = row.activeVersion;
    return {
        ...base,
        kind: 'TEMPLATE',
        status: row.status,
        category: row.category,
        versionText: version ? `v${version.version}` : NIL_TEXT,
        effectiveFromText: version
            ? isoToDisplayDate(version.effectiveFrom)
            : NIL_TEXT,
        pairsText: version ? String(version.pairCount) : NIL_TEXT,
        rolesAllowed: row.rolesAllowed,
        recipe: [],
        isEditable: canEdit,
    };
};

// ── chips ───────────────────────────────────────────────────────────────────

export const OPERATION_STATUS_CHIP: Record<
    OperationStatus,
    { className: string; labelKey: string }
> = {
    ACTIVE: {
        className: 'bg-success-soft text-success-soft-foreground',
        labelKey: 'operations.status.active',
    },
    DRAFT: {
        className: 'bg-warning-soft text-warning-soft-foreground',
        labelKey: 'operations.status.draft',
    },
    // Blocked is a settled decision, not an alarm — an operation deliberately
    // taken out of use, the way the chart archives an account. Muted, never
    // danger.
    BLOCKED: {
        className: 'bg-surface-secondary text-muted',
        labelKey: 'operations.status.blocked',
    },
};

/**
 * The chip a system row wears instead of a status. Neutral by design: it names
 * what the row *is*, and the one thing it must not suggest is a state that
 * could be changed.
 */
export const SYSTEM_CHIP = {
    className: 'bg-surface-secondary text-muted',
    labelKey: 'operations.kind.system',
};

/** i18n key for a role chip in the who-may-record column. */
export const roleLabelKey = (role: UserRole): string =>
    `operations.roles.${role}`;

// ── filtering ───────────────────────────────────────────────────────────────

/**
 * The status filter's options, in the order they appear.
 *
 * **These are template statuses, so every one but `ALL` hides the system rows** —
 * a system operation has no status to match. That is the correct behaviour and
 * the footer note says so, rather than the filter silently keeping nine rows
 * that no chip in the bar describes.
 */
export type StatusFilter = 'ALL' | OperationStatus;

export const STATUS_FILTERS: { id: StatusFilter; labelKey: string }[] = [
    { id: 'ALL', labelKey: 'operations.filters.all' },
    { id: 'ACTIVE', labelKey: 'operations.filters.active' },
    { id: 'DRAFT', labelKey: 'operations.filters.draft' },
    { id: 'BLOCKED', labelKey: 'operations.filters.blocked' },
];

/**
 * Search matches either name and the key, case-insensitively.
 *
 * The key is searchable because it is what the API, the seed file and any future
 * support conversation call the operation — someone told "check
 * `OWNER_DRAWING`" should be able to type that. Georgian is matched with the
 * same lowercase comparison as English; the script has no case, so the operation
 * is a no-op there rather than something that needs a separate path.
 */
export const matchesSearch = (row: OperationRow, search: string): boolean => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    return (
        row.nameKa.toLowerCase().includes(needle) ||
        row.nameEn.toLowerCase().includes(needle) ||
        row.key.toLowerCase().includes(needle)
    );
};

export const matchesStatus = (
    row: OperationRow,
    filter: StatusFilter,
): boolean => filter === 'ALL' || row.status === filter;

/**
 * Why the table is empty, which is not the same question as whether it is.
 *
 * `NONE` — there are rows. `NO_MATCH` — the catalog has rows and the filters hid
 * all of them, which the user can undo. `EMPTY` — the catalog itself came back
 * with nothing, which they cannot. Collapsing the last two into one "no results"
 * would tell someone to clear a search that was not the problem.
 */
export type TemplatesEmptyState = 'NONE' | 'NO_MATCH' | 'EMPTY';

export const emptyStateFor = (
    total: number,
    visible: number,
): TemplatesEmptyState => {
    if (visible > 0) {
        return 'NONE';
    }
    return total === 0 ? 'EMPTY' : 'NO_MATCH';
};

export const TEMPLATES_EMPTY_COPY: Record<
    Exclude<TemplatesEmptyState, 'NONE'>,
    { icon: string; titleKey: string; bodyKey: string }
> = {
    NO_MATCH: {
        icon: 'gravity-ui:magnifier',
        titleKey: 'operations.empty.noMatch.title',
        bodyKey: 'operations.empty.noMatch.body',
    },
    EMPTY: {
        icon: 'gravity-ui:layers',
        titleKey: 'operations.empty.catalog.title',
        bodyKey: 'operations.empty.catalog.body',
    },
};
